// File: src/pages/InvoiceTemplateSettings.jsx
import React, { useEffect, useState, useContext } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import swal from "sweetalert";

/* ======= Shared modules (as requested: everything else from ../import) ======= */
import UserContext from "../import/context/Context";
import { CheckPermission } from "../import/CheckPermission";
import {
  Create_CompanyDetails,
  ViewCompanyDetails,
} from "../import/ApiEndPoint/ApiCalling";

/* ======= Template preview images (keep or change paths to your assets) ======= */
import Templatethree from "../import/Templatethree.png";
import Templatetwo from "../import/Templatetwo.png";
import templatefour from "../import/templatefour.png";
import thformat from "../import/5thformat.jpg";
import templateone from "../import/templateone.png";

/* ======= Tiny inline CSS (no SCSS) ======= */
const injectOnce = () => {
  if (document.getElementById("invoice-template-inline-css")) return;
  const s = document.createElement("style");
  s.id = "invoice-template-inline-css";
  s.innerHTML = `
    .it-wrap { font-size: 12px; }
    .it-title { font-weight: 600; text-transform: uppercase; font-size: 14px; margin: 0; }
    .it-grid { display: grid; grid-template-columns: repeat(auto-fill,minmax(140px,1fr)); gap: 12px; }
    .it-thumb { border:1px solid #e5e7eb; border-radius:10px; padding:8px; cursor:pointer; transition: transform .15s ease, box-shadow .15s ease; background:#fff; }
    .it-thumb:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(0,0,0,.08); }
    .it-thumb.active { outline: 3px solid #00c0ef; outline-offset: 2px; background:#f0fcff; }
    .it-thumb img { width:100%; height:110px; object-fit: cover; border-radius:8px; }
    .it-toolbar { display:flex; gap:8px; justify-content:flex-end; }
    .muted { color:#6b7280; }
  `;
  document.head.appendChild(s);
};

/* ======= Helpers ======= */
const S = (v) => (v == null ? "" : String(v));
const normalizeLR = (v) => (S(v).toLowerCase() === "right" ? "Right" : "Left");

export default function InvoiceTemplateSettings() {
  injectOnce();
  const ctx = useContext(UserContext);
  const perms = CheckPermission("Setting");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Company/billing UI config
  const [logoPos, setLogoPos] = useState("Left");
  const [shipTo, setShipTo] = useState("Left");
  const [billTo, setBillTo] = useState("Right");
  const [billNumber, setBillNumber] = useState(1); // which template index (1..5)
  const [terms, setTerms] = useState(
    "1. Paid Amount/Payment are not refundable in any case.\n2. Pay Payment under 30 days."
  );

  // Banks
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState("");

  // Preview modal
  const [previewOpen, setPreviewOpen] = useState(false);

  /* ======= Bootstrap page with existing details + banks ======= */
  useEffect(() => {
    (async () => {
      try {
        const user = JSON.parse(localStorage.getItem("userData")) || {};
        // Load banks from your company details
        const res = await ViewCompanyDetails(user?._id, user?.database);
        const allBanks = res?.CompanyDetail?.bankDetails || [];
        setBanks(allBanks);

        // From context (if already set)
        const cd =
          ctx?.CompanyDetails ||
          JSON.parse(localStorage.getItem("Companydetail"));
        if (cd) {
          if (cd.imagePosition) setLogoPos(normalizeLR(cd.imagePosition));
          if (cd.billTo) setBillTo(normalizeLR(cd.billTo));
          if (cd.shipto) setShipTo(normalizeLR(cd.shipto));
          if (cd.BillNumber) setBillNumber(Number(cd.BillNumber) || 1);
          if (cd.termsAndCondition) setTerms(S(cd.termsAndCondition));
          if (cd.selectedBank) setSelectedBank(S(cd.selectedBank));
        } else {
          // default: if no context/local company details
          setLogoPos("Left");
          setBillTo("Right");
          setShipTo("Left");
          setBillNumber(1);
        }
      } catch {
        // non-fatal; keep defaults
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ======= Validate & Save ======= */
  const onSubmit = async (e) => {
    e.preventDefault();
    if (billTo === shipTo) {
      swal(
        "Validation",
        "Bill To and Ship To cannot be the same side.",
        "warning"
      );
      return;
    }
    if (!selectedBank) {
      swal(
        "Validation",
        "Please select a bank account for invoice.",
        "warning"
      );
      return;
    }

    try {
      setSaving(true);
      const user = JSON.parse(localStorage.getItem("userData")) || {};
      const formData = new FormData();
      formData.append("created_by", user?._id || "");
      formData.append("imagePosition", logoPos);
      formData.append("billTo", billTo);
      formData.append("shipto", shipTo);
      formData.append("BillNumber", billNumber);
      formData.append("termsAndCondition", terms);
      formData.append("selectedBank", selectedBank);

      const res = await Create_CompanyDetails(formData);

      const updated = res?.updateDetails || {
        imagePosition: logoPos,
        billTo,
        shipto: shipTo,
        BillNumber: billNumber,
        termsAndCondition: terms,
        selectedBank,
      };

      // sync context + localStorage
      if (ctx?.setCompanyDetails) {
        ctx.setCompanyDetails({
          ...(ctx.CompanyDetails || {}),
          ...updated,
        });
      }
      localStorage.setItem("Companydetail", JSON.stringify(updated));

      swal("Success", "Details are added successfully.", "success");
    } catch (err) {
      swal("Error", "Something went wrong while saving.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "12rem",
        }}
      >
        <Spinner style={{ width: "3rem", height: "3rem" }} />
      </div>
    );
  }

  return (
    <div className="it-wrap p-3">
      <Card>
        <CardBody>
          <Row className="align-items-center" style={{ marginBottom: 8 }}>
            <Col>
              <h3 className="it-title">Invoice Template</h3>
              <div className="muted">
                Configure logo/billing layout, bank account and terms.
              </div>
            </Col>
            <Col className="it-toolbar">
              <Button
                color="secondary"
                size="sm"
                onClick={() => setPreviewOpen(true)}
              >
                Preview
              </Button>
            </Col>
          </Row>

          <Form onSubmit={onSubmit}>
            <Row className="mb-2">
              <Col lg="4" md="6" className="mb-2">
                <Label>Logo Position</Label>
                <Input
                  required
                  type="select"
                  value={logoPos}
                  onChange={(e) => setLogoPos(normalizeLR(e.target.value))}
                >
                  <option value="">--- Select ---</option>
                  <option>Left</option>
                  <option>Right</option>
                </Input>
              </Col>

              <Col lg="4" md="6" className="mb-2">
                <Label>Ship To Position</Label>
                <Input
                  required
                  type="select"
                  value={shipTo}
                  onChange={(e) => setShipTo(normalizeLR(e.target.value))}
                >
                  <option value="">--- Select ---</option>
                  <option>Left</option>
                  <option>Right</option>
                </Input>
                {shipTo === billTo && (
                  <div style={{ color: "red", fontSize: 11, marginTop: 4 }}>
                    Bill To and Ship To cannot be same
                  </div>
                )}
              </Col>

              <Col lg="4" md="6" className="mb-2">
                <Label>Bill To Position</Label>
                <Input
                  required
                  type="select"
                  value={billTo}
                  onChange={(e) => setBillTo(normalizeLR(e.target.value))}
                >
                  <option value="">--- Select ---</option>
                  <option>Left</option>
                  <option>Right</option>
                </Input>
                {shipTo === billTo && (
                  <div style={{ color: "red", fontSize: 11, marginTop: 4 }}>
                    Bill To and Ship To cannot be same
                  </div>
                )}
              </Col>

              <Col lg="4" md="6" className="mb-2">
                <Label>Select Bank Account for Invoice</Label>
                <Input
                  required
                  type="select"
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value)}
                >
                  <option value="">-- Select Bank Account --</option>
                  {banks.map((b, i) => (
                    <option key={i} value={b.bankName}>
                      {b.bankName}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col sm="12" className="mb-2">
                <Label>Terms & Conditions</Label>
                <textarea
                  required
                  rows={5}
                  className="form-control"
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  placeholder={`1. Paid Amount/Payment are not refundable in any case.\n2. Pay Payment under 30 days.`}
                />
              </Col>
            </Row>

            {/* Templates grid */}
            <Row className="mb-3">
              <Col sm="12" className="mb-1">
                <Label>Choose Bill Template</Label>
              </Col>
              <Col sm="12">
                <div className="it-grid">
                  {[
                    Templatethree,
                    Templatetwo,
                    templateone,
                    templatefour,
                    thformat,
                  ].map((img, idx) => {
                    const n = idx + 1;
                    const active = billNumber === n;
                    return (
                      <div
                        key={n}
                        className={`it-thumb ${active ? "active" : ""}`}
                        onClick={() => setBillNumber(n)}
                        title={`Template ${n}`}
                      >
                        <img src={img} alt={`template-${n}`} />
                        <div style={{ marginTop: 6, fontWeight: 600 }}>
                          Template {n}
                        </div>
                        {active ? (
                          <div className="muted">Selected</div>
                        ) : (
                          <div className="muted">Click to select</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Col>
            </Row>

            <Row>
              <Col className="d-flex justify-content-center">
                <Button
                  type="submit"
                  color={saving ? "secondary" : "primary"}
                  disabled={saving}
                >
                  {saving ? "Updating..." : "Submit"}
                </Button>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>

      {/* Simple preview modal (static visual confirmation) */}
      <Modal
        isOpen={previewOpen}
        toggle={() => setPreviewOpen(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setPreviewOpen(false)}>Preview</ModalHeader>
        <ModalBody>
          <Row className="mb-2">
            <Col md="4">
              <div>
                <strong>Logo:</strong> {logoPos}
              </div>
              <div>
                <strong>Ship To:</strong> {shipTo}
              </div>
              <div>
                <strong>Bill To:</strong> {billTo}
              </div>
            </Col>
            <Col md="4">
              <div>
                <strong>Template:</strong> {billNumber}
              </div>
              <div>
                <strong>Bank:</strong> {selectedBank || "—"}
              </div>
            </Col>
            <Col md="4">
              <div>
                <strong>Terms:</strong>
              </div>
              <div style={{ whiteSpace: "pre-wrap" }} className="muted">
                {terms}
              </div>
            </Col>
          </Row>
          <Row>
            <Col sm="12">
              <img
                src={
                  [
                    Templatethree,
                    Templatetwo,
                    templateone,
                    templatefour,
                    thformat,
                  ][billNumber - 1]
                }
                alt="selected-template"
                style={{
                  width: "100%",
                  borderRadius: 10,
                  border: "1px solid #e5e7eb",
                }}
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPreviewOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
